/**
 * SA-717 (email triage spec §5, T18, T19): what triage decided for an email
 * thread, and a person's actions on it. One group per triaged message: the
 * first message, then each reply. Approve runs the proposed kind's handler;
 * Change kind picks another (each option says what it would do); Dismiss
 * (the action `not_for_us`) is the internal kind; Not an enquiry rejects the record a message created.
 * Reinstate undoes that (SA-770): the rejected row says who closed it and
 * when, and offers nothing else.
 * Actions need `inbox:triage`; the panel still reads without it. A kind whose
 * handler needs a permission the viewer lacks is disabled with the server's
 * sentence (SA-771), and Re-run shows on the server's `canRerun` alone
 * (SA-776: the flag already holds `inbox:manage` and the kind's own
 * permission).
 * SA-795: an action's request can take up to a minute (Approve on a kind that
 * creates a record runs the extraction and the record's birth), so the pressed
 * button shows a spinner and a present-tense label, every other action waits,
 * and the group's status line says what is happening in the server's words.
 * SA-946: on a WhatsApp group thread (the `group` prop) each row is headed by
 * its message, with a way to show it in the thread; Not an enquiry, Reinstate
 * and the record link sit on each row and act by its ledger id (SA-945 S2,
 * design note G1); the thread-level foot is not shown, and a closing line
 * counts the decided messages the window left out (G2).
 * SA-1030: a waiting group row that triage matched shows TriageMatchBlock in place of Approve.
 * SA-1042: a row whose waiting customer update carries a diff shows UpdateProposal; Accept posts the record's own apply (inbox:write).
 * SA-1095 PR 2: a closing kind (`outcomes`) shows a select under a visible "Close <record> as" label under a waiting row
 * (the record the server says it would close), and "Close as" under Change kind and above a close match block; Approve,
 * Apply and the block send the pick as `outcome` (null = close nothing). Other kinds send no key.
 * SA-1095: on an email thread of several open records, Change kind to a kind that adds the
 * message to a record (`takesTarget`) asks which one and sends its number with the decide;
 * a closing kind asks whenever any record is open.
 * SA-1295: on an email thread (the `email` prop) a row whose email is loaded is
 * headed by it in two lines (sender, time, a differing subject; then its own
 * text) with Show in thread, and folds to one cut line; `focusRow`, sent by an
 * email's chip, unfolds the panel without writing the remembered fold, opens
 * the row, scrolls the panel to it and rings it.
 */
import { Fragment, useEffect, useId, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/utils'
import { Badge, Button, Checkbox, Tip } from '@/components/ui'
import { usePermission } from '@/hooks/usePermission'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { triageErrorMessage, triageService } from '@/services/triage'
import type { InboxTriage, ThreadRecord, TriageKindOption, TriageRow } from '@/types/inbox'
import { outcomeLabel } from '@/components/process/stepLiveText'
import { COMPARE, compareHref, duplicateChipText, duplicateChipTitle } from '@/components/records/duplicateText'
import { customerUpdateErrorMessage, recordOnText, refusalWords, responseHref, updateHref } from '@/components/records/customerUpdateText'
import { UpdateProposal } from '@/components/records/UpdateProposal'
import type { CustomerUpdateApplyPayload } from '@/types/commerce'
import type { EmailRowMessage } from './emailRows'
import type { GroupRowMessage } from './groupMessages'
import { TriageMatchBlock } from './TriageMatchBlock'
import { BlockHeader, BlockQuote, Consequence, ProposalLine, ReceiptLine, RecordBand, WhyLine } from './TriageBlockParts'
import { blockState, kindOption, needsChoice, receiptChip } from './triageBlocks'
import { candidateFromRow } from './triageBulk'
import { TriageSelectionBar } from './TriageSelectionBar'
import { usePanelBulk } from './usePanelBulk'
import {
  CLOSED_NOT_ENQUIRY, closeText, DECIDING, KIND_REMOVED_LINE, QUEUED, ROW_CHANGED_MEANWHILE, selectLabel, consequenceParts, earlierLine, quoteFoot, emailRowName, openRecordsLead, threadRecordState, inReplyTo, intentLine, kindSentence, messagesText, nounWould, notClassifiedLine, outcomeLine, pickTargetLabel, receiptWords, recordBandLead, REPLY_ON_THREAD, targetText, waitingChipText, WORKING_LABEL, workingLine,
  type TriageAction,
} from './triageText'
import { tourAnchor } from '@/components/tour/tourAnchor'
import { PANEL_BESIDE, type PaneLayout } from './inboxLayout'

interface TriagePanelProps {
  conversationId: string
  triage: InboxTriage
  linkedOrderId: string | null
  linkedOrderNumber: string | null
  /** SA-1095: the thread's records; with several, each row acts on its own. */
  records?: ThreadRecord[]
  /** Called after a decision or a rejection went through. */
  onChanged: () => void
  /** SA-946: on a WhatsApp group thread, each row's message by ledger id, and
   * how to show one in the thread. Absent on email. */
  group?: {
    messages: Record<string, GroupRowMessage>
    onShowMessage: (messageId: string) => void
  }
  /** SA-1295: on an email thread, each row's email by ledger id, and how to show one in the thread. */
  email?: {
    messages: Record<string, EmailRowMessage>
    onShowMessage: (messageId: string) => void
  }
  /** SA-1295: a request from an email's chip to show its row; a fresh `seq` shows it again. */
  focusRow?: { ledgerId: string; seq: number } | null
  /** SA-1300: called with `focusRow` once the panel has shown its row, so the detail can drop it. */
  onFocusRowShown?: (request: { ledgerId: string; seq: number }) => void
  /** SA-1347: `beside` is a column of its own next to the thread: no fold, no height cap, its own scroll. */
  layout?: PaneLayout
}

const SELECT_CLASS =
  'text-xs rounded-md border border-border bg-background px-2 py-1 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring'

/** The panel foot's group (Not an enquiry, Reinstate); a message's group is
 * scoped by its ledger id. */
const FOOT = 'record'

/** SA-1295: a row's element, so a chip's request can scroll to it. */
const rowDomId = (ledgerId: string) => `triage-row-${ledgerId}`
/** SA-1329: a row's match block, so "Pick the record…" can move to it. */
const matchDomId = (ledgerId: string) => `triage-match-${ledgerId}`

/** SA-1079: whether this viewer folded the panel, per device. Every access
 * is guarded: a private window or a blocked store reads as open. */
const FOLD_KEY = 'inbox-triage:folded'
function readFolded(): boolean {
  try { return window.localStorage.getItem(FOLD_KEY) === 'folded' } catch { return false }
}
function storeFolded(folded: boolean): void {
  try { window.localStorage.setItem(FOLD_KEY, folded ? 'folded' : 'open') } catch { /* unavailable */ }
}

/** The request the panel is holding (SA-795). */
interface Pending {
  action: TriageAction
  scope: string
  /** The status line, worded when the action was pressed. */
  line: string
}

export function TriagePanel({ conversationId, triage, linkedOrderId, linkedOrderNumber, onChanged, group, email, focusRow = null, onFocusRowShown, records = [], layout = 'stacked' }: TriagePanelProps) {
  const canTriage = usePermission('inbox:triage')
  const canWrite = usePermission('inbox:write')
  const noun = useRecordNoun().singular.toLowerCase()
  const zone = useDefaultTimezone()
  const [pending, setPending] = useState<Pending | null>(null)
  const busy = pending !== null
  const [error, setError] = useState<string | null>(null)
  // The row whose "Change kind" select is open, and what it has picked.
  const [changing, setChanging] = useState<string | null>(null)
  const [picked, setPicked] = useState('')
  // SA-1095 (final review, ruling 3): the record a picked update kind lands on.
  const [pickedRecord, setPickedRecord] = useState('')
  // SA-1095 PR 2: a closing kind's outcome. A row's pick (by ledger id) starts at its stored outcome; Change kind's starts empty.
  const [outcomePicks, setOutcomePicks] = useState<Record<string, string>>({})
  const [changeOutcome, setChangeOutcome] = useState('')
  // Each outcome select's own id, so its visible label names it.
  const outcomeIds = useId()
  // SA-1079 (spec §3.8): on a thread of several messages the decided rows
  // fold to one line and a waiting row stays open; the header folds the
  // panel whole. Open rows are state, remounted per conversation (the key).
  const multi = triage.rows.length > 1
  // A row whose update is offered for Accept stays open like a waiting row: the diff is the work (SA-1042).
  const offersUpdate = (row: TriageRow) => !!row.customerUpdate?.canApply && (row.customerUpdate.changes?.length ?? 0) > 0
  const [openRows, setOpenRows] = useState<Set<string>>(() => new Set())
  const [folded, setFolded] = useState<boolean>(readFolded)
  // Beside the thread the panel is always open and the fold preference is neither read nor written.
  const beside = layout === 'beside'
  const panelOpen = beside || !folded
  const toggleRow = (id: string) => setOpenRows((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const toggleFolded = () => setFolded((prev) => { storeFolded(!prev); return !prev })

  // SA-1295: an email's chip asks to show its row. Unfold without writing the
  // remembered fold (the RecordSection `focused` precedent), open the row, then
  // scroll the panel to it and ring it for the email ring's 2.5 s.
  // The scroll waits for the commit that draws the row: a folded panel has
  // none in the DOM until its unfold renders.
  // SA-1308: the request a ring answers, so a repeated one restarts its 2.5 s.
  // SA-1300: the detail drops a request the panel has shown, so a row coming
  // back or a remount never replays it.
  const [ringedRow, setRingedRow] = useState<{ ledgerId: string; seq: number } | null>(null)
  const [scrollTarget, setScrollTarget] = useState<{ ledgerId: string; seq: number } | null>(null)
  const knownRow = !!focusRow && triage.rows.some((r) => r.ledgerId === focusRow.ledgerId)
  useEffect(() => {
    if (!focusRow || !knownRow) return
    const id = focusRow.ledgerId
    setFolded(false)
    setOpenRows((prev) => (prev.has(id) ? prev : new Set(prev).add(id)))
    setScrollTarget(focusRow)
    onFocusRowShown?.(focusRow)
  }, [focusRow, knownRow, onFocusRowShown])
  useEffect(() => {
    if (!scrollTarget || !panelOpen) return
    const target = scrollTarget
    const frame = requestAnimationFrame(() => {
      document.getElementById(rowDomId(target.ledgerId))?.scrollIntoView?.({ block: 'nearest' })
      setRingedRow(target)
      setScrollTarget(null)
    })
    return () => cancelAnimationFrame(frame)
  }, [scrollTarget, panelOpen])
  useEffect(() => {
    if (!ringedRow) return
    const timer = window.setTimeout(() => setRingedRow(null), 2500)
    return () => window.clearTimeout(timer)
  }, [ringedRow])

  const run = async (next: Pending, call: () => Promise<unknown>, wordError: (err: unknown) => string = triageErrorMessage) => {
    setPending(next)
    setError(null)
    try {
      await call()
      setChanging(null)
      setPicked('')
      setPickedRecord('')
      setChangeOutcome('')
      onChanged()
    } catch (err) {
      setError(wordError(err))
    } finally {
      setPending(null)
    }
  }

  const working = (action: TriageAction, scope: string) => pending?.action === action && pending.scope === scope
  /** The pressed button's label while it works, else its own. */
  const label = (action: TriageAction, scope: string, idle: string) =>
    (working(action, scope) ? WORKING_LABEL[action] : idle)
  /** What deciding a kind would do, in the server's words. A row's own
   * `would` is sent only while the row is proposed. */
  // Every handler words its record as "record"; the working lines and the Change kind sentence put the noun in (SA-1329).
  const nounWouldOf = (would: string | null | undefined) => (would ? nounWould(would, noun) : would)
  const kindWould = (id: string | null) => nounWouldOf(triage.kinds.find((kind) => kind.id === id)?.would ?? null)
  const record = linkedOrderNumber ? `${noun} ${linkedOrderNumber}` : `this thread's ${noun}`

  const handleReject = () => {
    if (!window.confirm(`Close ${record} as not an enquiry? It will be hidden from the register.`)) return
    void run({ action: 'reject', scope: FOOT, line: workingLine('reject', { record }) },
      () => triageService.reject(conversationId, {}))
  }

  const handleReinstate = () => {
    if (!window.confirm(`Reinstate ${record} and return it to the register?`)) return
    void run({ action: 'reinstate', scope: FOOT, line: workingLine('reinstate', { record }) },
      () => triageService.reinstate(conversationId))
  }

  const rejectable = triage.canReject && canTriage && canWrite
  const reinstatable = triage.canReinstate && canTriage && canWrite
  const rowRejectable = (row: TriageRow) => !!row.canReject && canTriage && canWrite
  const rowReinstatable = (row: TriageRow) => !!row.canReinstate && canTriage && canWrite
  const rowRecord = (row: TriageRow) => (row.recordNumber ? `${noun} ${row.recordNumber}` : `this message's ${noun}`)

  const handleRowReject = (row: TriageRow) => {
    const named = rowRecord(row)
    if (!window.confirm(`Close ${named} as not an enquiry? It will be hidden from the register.`)) return
    void run({ action: 'reject', scope: row.ledgerId, line: workingLine('reject', { record: named }) },
      () => triageService.reject(conversationId, { ledger_id: row.ledgerId }))
  }

  const handleRowReinstate = (row: TriageRow) => {
    const named = rowRecord(row)
    if (!window.confirm(`Reinstate ${named} and return it to the register?`)) return
    void run({ action: 'reinstate', scope: row.ledgerId, line: workingLine('reinstate', { record: named }) },
      () => triageService.reinstate(conversationId, { ledger_id: row.ledgerId }))
  }

  // SA-1042 (spec §D12): the record's own apply endpoint, the Intake block's body. One place to apply.
  const handleAcceptUpdate = (row: TriageRow, payload: CustomerUpdateApplyPayload) => {
    const update = row.customerUpdate
    if (!update) return
    const number = row.recordNumber ?? linkedOrderNumber
    void run(
      { action: 'accept_update', scope: row.ledgerId,
        line: workingLine('accept_update', { record: number ? `${noun} ${number}` : `this thread's ${noun}` }) },
      () => triageService.acceptUpdate(update.orderId, update.id, payload),
      customerUpdateErrorMessage)
  }

  /** SA-1030: send the message to a record as an update, or make it a new one. */
  const handleUpdate = (row: TriageRow, recordNumber: string) => {
    const updateKind = row.match?.updateKind
    if (!updateKind) return
    const closing = row.match?.action === 'close'
    const action = closing ? 'close' : 'update'
    const body = { ledger_id: row.ledgerId, kind: updateKind, record_number: recordNumber,
      ...(hasOutcomes(updateKind) ? { outcome: outcomeOf(row) || null } : {}) }
    void run({ action, scope: row.ledgerId, line: workingLine(action, { record: `${noun} ${recordNumber}` }) },
      () => triageService.decide(conversationId, body))
  }

  const handleCreate = (row: TriageRow) => {
    const newKind = row.match?.newKind
    if (!newKind) return
    void run({ action: 'create', scope: row.ledgerId,
      line: workingLine('create', { label: row.match?.newLabel ?? noun, would: kindWould(newKind) }) },
    () => triageService.decide(conversationId, { ledger_id: row.ledgerId, kind: newKind }))
  }

  /** SA-1095 PR 2: a kind's outcomes (empty for every kind that closes nothing). */
  const outcomesOf = (id: string | null | undefined): string[] => triage.kinds.find((kind) => kind.id === id)?.outcomes ?? []
  const hasOutcomes = (id: string | null | undefined) => outcomesOf(id).length > 0
  /** The row's pick: what the person chose, else the stored proposal, else none. */
  const outcomeOf = (row: TriageRow) => outcomePicks[row.ledgerId] ?? row.outcome ?? ''
  /** Final review (Critical 1): the label is visible ("Close record Q-1042 as"), so the approver
   * sees which record the close lands on; it is also the select's accessible name. */
  const outcomeSelect = (id: string, name: string, codes: string[], value: string, onPick: (code: string) => void) => {
    const selectId = `${outcomeIds}-${id}`
    return (
      <span className="inline-flex items-center gap-1.5">
        <label htmlFor={selectId} className="text-xs text-foreground">{name}</label>
        <select id={selectId} value={value} disabled={busy} onChange={(e) => onPick(e.target.value)} className={SELECT_CLASS}>
          <option value="">{closeText.nothing}</option>
          {codes.map((code) => <option key={code} value={code}>{outcomeLabel(code) ?? code}</option>)}
        </select>
      </span>
    )
  }

  /** SA-1295: an email row's email, when the thread loaded it. */
  const emailOf = (row: TriageRow): EmailRowMessage | undefined => (group ? undefined : email?.messages[row.ledgerId])
  /** A row's heading: on a group its message, on email its email, else first message or reply. */
  const heading = (row: TriageRow): string => {
    const mail = emailOf(row)
    if (mail) return emailRowName(mail)
    if (!group) return row.isReply ? 'Reply' : 'First message'
    const message = group.messages[row.ledgerId]
    return message ? `${message.sender ?? 'Someone'} · “${message.excerpt}”` : 'A message'
  }

  /** SA-1329: a waiting block's sender: the email's or the group message's, else its heading. */
  const blockSender = (row: TriageRow): string => emailOf(row)?.sender ?? group?.messages[row.ledgerId]?.sender ?? heading(row)
  /** SA-1329: what sits beside the time: a reply on an email thread and a differing subject, or whom a group message answers. */
  const blockContext = (row: TriageRow): string | null => {
    const mail = emailOf(row)
    if (mail) return [row.isReply ? REPLY_ON_THREAD : null, mail.subject].filter((part): part is string => !!part).join(' · ') || null
    const replyTo = group?.messages[row.ledgerId]?.replyTo
    return replyTo ? inReplyTo(replyTo) : null
  }

  const waitingCount = triage.rows.filter((row) => blockState(row) === 'waiting').length
  // §9.6: the thread-level foot only when no row names the thread's record (a record born before SA-1095 served it per row).
  const rowNamesRecord = !!linkedOrderId && triage.rows.some((r) => r.recordId === linkedOrderId)
  const showFoot = !group && records.length <= 1 && !rowNamesRecord && !!(linkedOrderId || rejectable || reinstatable)
  const pickedKind = triage.kinds.find((kind) => kind.id === picked)
  // SA-1095 (final review, ruling 3): on an email thread of several open records a kind that
  // adds the message to a record names which one, or the decide is refused target_required.
  // A closing kind asks whenever any record is open (PR 2 final review): the answer's own
  // record may be closed while another is open, and one open record is still a pick.
  const openRecords = records.filter((rec) => rec.state === 'open')
  // SA-1329: the blocks a person can select, and the bulk run over them.
  const cands = triage.rows.flatMap((row) => {
    const cand = candidateFromRow(row, triage, { conversationId, canTriage, noun, openRecords, label: heading(row) })
    return cand ? [cand] : []
  })
  const candOf = (row: TriageRow) => cands.find((c) => c.key === row.ledgerId)
  const bulk = usePanelBulk({ conversationId, triage, cands, noun, outcomePicks })
  /** The run holds this block's message while it waits or works. */
  const runHolds = (row: TriageRow) => {
    const item = bulk.itemFor(row.ledgerId)
    return item?.state === 'queued' || item?.state === 'deciding'
  }
  /** A block stays open while its run item waits, works or holds a sentence; a done one folds to its receipt (spec 4.3). */
  const runKeepsOpen = (row: TriageRow) => {
    const state = bulk.itemFor(row.ledgerId)?.state
    return state === 'queued' || state === 'deciding' || state === 'conflict' || state === 'refused'
  }
  // SA-1229: the union rule: the server's per-row flag (it counts records the viewer cannot see) or the visible count.
  const needsTargetFor = (row: TriageRow): boolean => {
    if (group || !pickedKind?.takesTarget) return false
    const closing = hasOutcomes(picked)
    return (closing ? row.targetNeeded?.close : row.targetNeeded?.update) === true
      || openRecords.length >= (closing ? 1 : 2)
  }
  /** The kind's option when this viewer may not decide it (SA-771). */
  const refused = (id: string | null): TriageKindOption | undefined =>
    triage.kinds.find((kind) => kind.id === id && !kind.canDecide)
  /** The refused options among these, each once, for their sentences. */
  const refusals = (options: (TriageKindOption | undefined)[]) =>
    options.filter((option, index): option is TriageKindOption =>
      !!option?.refusal && options.findIndex((other) => other?.id === option.id) === index)
  const refusalId = (row: TriageRow, option: TriageKindOption) => `triage-${row.ledgerId}-needs-${option.id}`

  /** A group's status line (D3): always mounted, so the words land in a live
   * region that already exists; empty unless this group is working. SA-808:
   * a message group's line also stays while its own request is held, though
   * a refresh (a `savepoint = False` handler's committed claim) makes the
   * row undecidable meanwhile. */
  const runLine = (scope: string): string | null => {
    const item = bulk.itemFor(scope)
    if (!item) return null
    if (item.state === 'queued') return QUEUED
    if (item.state === 'deciding') return DECIDING
    if (item.state === 'conflict') return ROW_CHANGED_MEANWHILE
    if (item.state === 'refused') return item.sentence
    return null
  }
  const statusLine = (scope: string) => (
    <p role="status" className="text-xs text-muted-foreground">
      {pending?.scope === scope ? pending.line : runLine(scope)}
    </p>
  )

  const actions = (row: TriageRow, decidable: boolean) => {
    const scope = row.ledgerId
    const held = runHolds(row)
    // SA-1411: a proposed kind the policy no longer holds could only be refused, so no Approve; a line says why.
    const removed = decidable && row.decisionStatus === 'proposed' && !!row.proposedKind && !row.match
      && !kindOption(triage.kinds, row.proposedKind)
    const approve = decidable && row.decisionStatus === 'proposed' && !!row.proposedKind && !row.match && !removed
    // SA-1095 PR 2: Approve on a closing kind sends the pick; any other kind sends no outcome key.
    const approveBody = hasOutcomes(row.proposedKind)
      ? { ledger_id: row.ledgerId, outcome: outcomeOf(row) || null }
      : { ledger_id: row.ledgerId }
    const notForUs = decidable && row.kind !== 'internal'
    const blockedApprove = approve ? refused(row.proposedKind) : undefined
    const blockedNotForUs = notForUs ? refused('internal') : undefined
    const pickRecord = decidable && row.decisionStatus === 'proposed' && !!row.match
    return (
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {removed && <p className="text-xs text-muted-foreground">{KIND_REMOVED_LINE}</p>}
        {approve && hasOutcomes(row.proposedKind) && outcomeSelect(
          `${row.ledgerId}-approve`, closeText.label(noun, row.recordNumber ?? undefined),
          outcomesOf(row.proposedKind), outcomeOf(row),
          (code) => setOutcomePicks((prev) => ({ ...prev, [row.ledgerId]: code })))}
        {approve && (
          <Button size="sm" disabled={busy || held || !!blockedApprove} isLoading={working('approve', scope)} className="text-xs" {...tourAnchor('inbox.triage.approve')}
            aria-describedby={blockedApprove ? refusalId(row, blockedApprove) : undefined}
            onClick={() => run({ action: 'approve', scope, line: workingLine('approve', { would: nounWouldOf(row.would) }) },
              () => triageService.decide(conversationId, approveBody))}>
            {label('approve', scope, 'Approve')}
          </Button>
        )}
        {pickRecord && (
          <Button size="sm" disabled={busy || held} className="text-xs" onClick={() => {
            const block = document.getElementById(matchDomId(row.ledgerId))
            block?.scrollIntoView?.({ block: 'nearest' })
            block?.querySelector<HTMLElement>('input')?.focus()
          }}>
            {pickTargetLabel(noun)}
          </Button>
        )}
        {decidable && (
          <Button size="sm" variant="secondary" disabled={busy || held} className="text-xs"
            aria-expanded={changing === row.ledgerId} {...tourAnchor('inbox.triage.change-kind')}
            onClick={() => { setChanging(changing === row.ledgerId ? null : row.ledgerId); setPicked(''); setPickedRecord('') }}>
            Change kind…
          </Button>
        )}
        {row.canRerun && (
          <Button size="sm" variant="secondary" disabled={busy || held} isLoading={working('rerun', scope)} className="text-xs"
            onClick={() => run({ action: 'rerun', scope, line: workingLine('rerun', { would: kindWould(row.kind) }) },
              () => triageService.rerun(triage.mailboxId, row.ledgerId))}>
            {label('rerun', scope, 'Re-run')}
          </Button>
        )}
        {notForUs && (
          <Button size="sm" variant="ghost" disabled={busy || held || !!blockedNotForUs}
            isLoading={working('not_for_us', scope)} className="ml-auto text-xs" {...tourAnchor('inbox.triage.dismiss')}
            aria-describedby={blockedNotForUs ? refusalId(row, blockedNotForUs) : undefined}
            onClick={() => run(
              { action: 'not_for_us', scope, line: workingLine('not_for_us', { would: kindWould('internal') }) },
              () => triageService.decide(conversationId, { ledger_id: row.ledgerId, kind: 'internal' }))}>
            {label('not_for_us', scope, 'Dismiss')}
          </Button>
        )}
        {refusals([blockedApprove, blockedNotForUs]).map((option) => (
          <p key={option.id} id={refusalId(row, option)} className="basis-full text-xs text-muted-foreground">
            {option.refusal}
          </p>
        ))}
      </div>
    )
  }

  const foldButton = (
    <Tip text={folded ? 'Show triage' : 'Hide triage'}>
      <button type="button" aria-expanded={!folded} aria-label={folded ? 'Show triage' : 'Hide triage'} onClick={toggleFolded}
        className="rounded-sm p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {folded ? <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
      </button>
    </Tip>
  )

  return (
    <div
      role="region"
      aria-label="Triage"
      {...tourAnchor('inbox.triage.panel')}
      className={beside
        ? cn('bg-muted/40 px-4 py-3 space-y-3', PANEL_BESIDE)
        : cn('border-t border-border bg-muted/40 px-4 py-3 space-y-3', !folded && 'max-h-[45%] overflow-y-auto')}
    >
      <div className="sticky top-0 z-10 -mx-4 -mt-3 bg-background">
      {bulk.barOn ? (
        <div className="flex items-start gap-1 bg-muted/40 pr-4">
          <div className="min-w-0 flex-1"><TriageSelectionBar {...bulk.barProps} /></div>
          {!beside && <span className="pt-3">{foldButton}</span>}
        </div>
      ) : (
      <div className="flex items-center gap-2 bg-muted/40 px-4 pt-3 pb-1">
        <p className="text-sm font-semibold text-foreground">Triage</p>
        {waitingCount > 0 && <Badge variant="warning-soft">{waitingChipText(waitingCount)}</Badge>}
        <p className="text-xs text-muted-foreground">{messagesText(triage.rows.length)}</p>
        {!beside && <span className="ml-auto">{foldButton}</span>}
      </div>
      )}
      </div>
      {panelOpen && error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      {panelOpen && triage.rows.map((row) => {
        const state = blockState(row)
        const waiting = state === 'waiting'
        // A row mid-action stays open: a savepoint-free handler commits the decision before its run returns (SA-808).
        const foldable = multi && !waiting && !offersUpdate(row) && pending?.scope !== row.ledgerId && !runKeepsOpen(row)
        const open = !foldable || openRows.has(row.ledgerId)
        const lines = [notClassifiedLine(row), intentLine(row), outcomeLine(row)]
          .filter((line): line is string => !!line)
        // A picked outcome answers an outcome need only; a record still needs picking.
        const needed = needsChoice(row, triage.kinds)
        const choice = needed === 'outcome' && outcomePicks[row.ledgerId] !== undefined ? null : needed
        const groupMsg = group?.messages[row.ledgerId]
        const decidable = canTriage && row.canDecide
        const cand = candOf(row)
        const selected = !!cand && bulk.sel.isSelected(cand.key)
        const selectBox = cand ? (
          <Checkbox checked={selected} aria-label={selectLabel(heading(row))} disabled={bulk.running}
            onCheckedChange={() => {}}
            onClick={(e) => { e.preventDefault(); bulk.sel.toggle(cand.key, e.shiftKey) }} />
        ) : null
        const needsTarget = changing === row.ledgerId && needsTargetFor(row)
        // The match block owns sending a message to a record, so Change kind leaves the update kind out (F7).
        const choices = triage.kinds.filter((kind) => kind.id !== row.kind && kind.id !== row.match?.updateKind)
        const mail = emailOf(row)
        const ringed = ringedRow?.ledgerId === row.ledgerId
        const onShow = groupMsg ? () => group?.onShowMessage(groupMsg.messageId)
          : mail && email ? () => email.onShowMessage(mail.messageId) : undefined
        const quote = mail ? (
          <BlockQuote lines={mail.lines.length ? mail.lines : [mail.text]} clamp={!mail.lines.length}
            foot={quoteFoot(mail.moreLines, mail.fileCount)} testId={mail.lines.length ? 'email-row-lines' : undefined} />
        ) : groupMsg ? <BlockQuote lines={[groupMsg.text]} clamp foot={null} /> : null
        const rec = records.find((r) => r.id === row.recordId)
        const recordState = row.handlerOutcome === 'rejected' ? CLOSED_NOT_ENQUIRY : rec ? threadRecordState(rec) : null
        const showBand = !!row.recordId || rowRejectable(row) || rowReinstatable(row)
        const bandEl = showBand ? (
          <RecordBand lead={recordBandLead(!!kindOption(triage.kinds, waiting ? row.proposedKind : row.kind)?.takesTarget)}
            number={row.recordNumber ?? null} state={recordState}
            openHref={row.recordId ? `/orders?order=${row.recordId}` : null} noun={noun} busy={busy}
            working={working('reject', row.ledgerId) ? 'reject' : working('reinstate', row.ledgerId) ? 'reinstate' : null}
            onReject={rowRejectable(row) ? () => handleRowReject(row) : undefined}
            onReinstate={rowReinstatable(row) ? () => handleRowReinstate(row) : undefined} />
        ) : null
        return (
          <div key={row.ledgerId} id={rowDomId(row.ledgerId)} role="group" aria-label={heading(row)}
            data-focused={ringed ? 'true' : undefined}
            // scroll-mt-10 keeps a scrolled-to row's heading clear of the sticky header.
            className={cn('rounded-md scroll-mt-10 transition-shadow', ringed && 'ring-2 ring-primary',
              waiting ? 'space-y-2 border border-border border-l-[3px] p-3' : 'space-y-1.5 border border-border px-3 py-2',
              selected ? 'bg-accent border-l-[3px] border-l-primary' : 'bg-card',
              waiting && !selected && 'border-l-warning')}>
            {waiting ? (<>
              <BlockHeader select={selectBox} sender={blockSender(row)} when={groupMsg?.when ?? mail?.when ?? null} context={blockContext(row)}
                onShow={onShow} />
              {quote}
            </>) : (<>
              <ReceiptLine select={selectBox} chip={receiptChip(row)} sender={blockSender(row)} when={groupMsg?.when ?? mail?.when ?? null}
                words={receiptWords(row, undefined, zone)} open={open}
                onToggle={foldable ? () => toggleRow(row.ledgerId) : undefined} onShow={onShow} />
              {open && quote}
            </>)}
            {row.duplicate && (row.handlerRef ?? row.recordId ?? linkedOrderId) && (
              <p className="flex flex-wrap items-center gap-2 text-xs" data-testid="duplicate-chip">
                <Tip text={duplicateChipTitle(row.duplicate)}>
                  <Badge variant="warning-soft" tabIndex={0} className="px-1.5 py-0 text-[10px]">{duplicateChipText(row.duplicate)}</Badge>
                </Tip>
                <Link to={compareHref((row.handlerRef ?? row.recordId ?? linkedOrderId) as string)}
                  className="font-medium text-primary underline-offset-2 hover:underline">
                  {COMPARE}
                </Link>
              </p>
            )}
            {open && (<>
            {waiting ? (<>
              <ProposalLine row={row} option={kindOption(triage.kinds, row.proposedKind)} choice={choice} noun={noun} />
              <Consequence parts={consequenceParts(row.would, noun)} />
              {lines.map((line) => <p key={line} className="text-xs text-foreground">{line}</p>)}
              {row.rationale && <WhyLine rationale={row.rationale} />}
            </>) : (<>
              {row.handlerOutcome !== 'rejected' && (
                <ProposalLine row={row} option={kindOption(triage.kinds, row.kind)} choice={null} noun={noun} decided />
              )}
              {lines.map((line) => <p key={line} className="text-xs text-foreground">{line}</p>)}
              {row.rationale && <WhyLine rationale={row.rationale} />}
            </>)}
            {row.handlerLink && (
              <Link to={row.handlerLink.href} className="block text-xs font-medium text-primary underline-offset-2 hover:underline">
                {row.handlerLink.label}
              </Link>
            )}
            {row.customerUpdate?.kind === 'response' ? (
              // SA-1047 (spec §D10): the answer is recorded on the record's Quote versions, never accepted here.
              <>
                <Link to={responseHref(row.customerUpdate.orderId)} className="block text-xs font-medium text-primary underline-offset-2 hover:underline">
                  {recordOnText((row.recordNumber ?? linkedOrderNumber) ?? noun)}
                </Link>
              </>
            ) : row.customerUpdate && offersUpdate(row) ? (
              <UpdateProposal
                key={`${row.customerUpdate.id}:${(row.customerUpdate.changes ?? []).map((c) => c.field.id).join(',')}`}
                changes={row.customerUpdate.changes ?? []} busy={busy} accepting={working('accept_update', row.ledgerId)}
                revision={row.customerUpdate.revises}
                editHref={updateHref(row.customerUpdate.orderId)}
                onAccept={canWrite ? (payload) => handleAcceptUpdate(row, payload) : undefined} />
            ) : row.customerUpdate && (
              <>
                <Link to={updateHref(row.customerUpdate.orderId)} className="block text-xs font-medium text-primary underline-offset-2 hover:underline">
                  Review update on {noun}{(row.recordNumber ?? linkedOrderNumber) ? ` ${row.recordNumber ?? linkedOrderNumber}` : ''}
                </Link>
                {!row.customerUpdate.canApply && row.customerUpdate.applyRefusal && (
                  <p className="text-xs text-muted-foreground">{refusalWords(row.customerUpdate.applyRefusal)}</p>
                )}
              </>
            )}
            {waiting && row.threadRecords && row.threadRecords.length > 0 && (
              <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                <span>{openRecordsLead(noun, !!group)}</span>
                {row.threadRecords.map((rec) => (
                  <Fragment key={rec.id}>
                  <Link to={`/orders?order=${rec.id}`} className="font-medium text-primary underline-offset-2 hover:underline">
                    {noun} {rec.number}
                  </Link>
                  <span>({threadRecordState(rec)})</span>
                  </Fragment>
                ))}
              </p>
            )}
            {decidable && row.match?.action === 'close' && hasOutcomes(row.match.updateKind) && (
              <div className="flex items-center gap-2 pt-1">
                {outcomeSelect(`${row.ledgerId}-match`, closeText.label(noun), outcomesOf(row.match.updateKind),
                  outcomeOf(row), (code) => setOutcomePicks((prev) => ({ ...prev, [row.ledgerId]: code })))}
              </div>
            )}
            {decidable && row.match && (
              <div id={matchDomId(row.ledgerId)}>
              <TriageMatchBlock
                key={`${row.ledgerId}:${row.match.candidates.map((c) => `${c.recordId}${c.likely || c.cited ? '*' : ''}`).join(',')}`}
                match={row.match} proposedKind={row.proposedKind} kinds={triage.kinds} noun={noun} busy={busy}
                working={working('update', row.ledgerId) ? 'update' : working('close', row.ledgerId) ? 'close' : working('create', row.ledgerId) ? 'create' : null}
                onUpdate={(recordNumber) => handleUpdate(row, recordNumber)} onCreate={() => handleCreate(row)} />
              </div>
            )}
            {(decidable || row.canRerun) && actions(row, decidable)}
            {decidable && changing === row.ledgerId && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <select aria-label="Kind" value={picked} disabled={busy}
                  onChange={(e) => { setPicked(e.target.value); setPickedRecord(''); setChangeOutcome('') }}
                  className={SELECT_CLASS}>
                  <option value="">Select a kind…</option>
                  {choices.map((kind) => (
                    <option key={kind.id} value={kind.id} disabled={!kind.canDecide}>{kind.label}</option>
                  ))}
                </select>
                {needsTarget && openRecords.length === 0 && (
                  <p className="basis-full text-xs text-muted-foreground">{targetText.noneVisible(noun)}</p>
                )}
                {needsTarget && openRecords.length > 0 && (
                  <select aria-label={targetText.label(noun)} required value={pickedRecord} disabled={busy}
                    onChange={(e) => setPickedRecord(e.target.value)} className={SELECT_CLASS}>
                    <option value="">{targetText.choose(noun)}</option>
                    {openRecords.map((rec) => (
                      <option key={rec.id} value={rec.number}>{rec.number}</option>
                    ))}
                  </select>
                )}
                {hasOutcomes(picked) && outcomeSelect(
                  `${row.ledgerId}-change`, closeText.label(noun), outcomesOf(picked), changeOutcome, setChangeOutcome)}
                <Button size="sm" disabled={busy || !pickedKind?.canDecide || (needsTarget && !pickedRecord)}
                  isLoading={working('apply', row.ledgerId)}
                  className="text-xs"
                  onClick={() => {
                    // Disabled without a decidable picked kind; this narrows it for the words.
                    if (!pickedKind) return
                    const body = {
                      ledger_id: row.ledgerId, kind: picked,
                      ...(needsTarget ? { record_number: pickedRecord } : {}),
                      ...(hasOutcomes(picked) ? { outcome: changeOutcome || null } : {}),
                    }
                    void run(
                      { action: 'apply', scope: row.ledgerId,
                        line: workingLine('apply', { would: nounWouldOf(pickedKind.would), label: pickedKind.label }) },
                      () => triageService.decide(conversationId, body))
                  }}>
                  {label('apply', row.ledgerId, 'Apply')}
                </Button>
                {pickedKind && kindSentence(nounWould(pickedKind.would, noun)) && (
                  <p className="basis-full text-xs text-muted-foreground">{kindSentence(nounWould(pickedKind.would, noun))}</p>
                )}
                {refusals(choices).map((option) => (
                  <p key={option.id} className="basis-full text-xs text-muted-foreground">{option.refusal}</p>
                ))}
              </div>
            )}
            {waiting && bandEl}
            {(decidable || row.canRerun || rowRejectable(row) || rowReinstatable(row) || pending?.scope === row.ledgerId || !!bulk.itemFor(row.ledgerId))
              && statusLine(row.ledgerId)}
            </>)}
            {!waiting && bandEl}
          </div>
        )
      })}
      {panelOpen && showFoot && (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            {linkedOrderId && (
              <Link to={`/orders?order=${linkedOrderId}`} className="text-xs font-medium text-primary underline-offset-2 hover:underline">
                Open {noun}{linkedOrderNumber ? ` ${linkedOrderNumber}` : ''}
              </Link>
            )}
            {rejectable && (
              <Button size="sm" variant="secondary" disabled={busy} isLoading={working('reject', FOOT)}
                onClick={handleReject} className="text-xs">
                {label('reject', FOOT, 'Not an enquiry')}
              </Button>
            )}
            {reinstatable && (
              <Button size="sm" variant="secondary" disabled={busy} isLoading={working('reinstate', FOOT)}
                onClick={handleReinstate} className="text-xs">
                {label('reinstate', FOOT, `Reinstate ${linkedOrderNumber ?? noun}`)}
              </Button>
            )}
          </div>
          {(rejectable || reinstatable) && statusLine(FOOT)}
        </div>
      )}
      {panelOpen && group && (triage.earlierDecided ?? 0) > 0 && (
        <p className="text-xs text-muted-foreground">{earlierLine(triage.earlierDecided ?? 0)}</p>
      )}
    </div>
  )
}
